import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { createAdminAgeGroup, fetchAdminAgeGroups, updateAdminAgeGroup, updateAdminAgeGroupStatus } from "@/admin/api";
import type { AdminAgeGroup } from "@/admin/types";
import { getAgeRangeLabel } from "@/constants/ui";
import LanguageSelect from "@/components/feature/LanguageSelect";
import Modal from "@/components/ui/Modal";
import AdminListCard from "@/pages/admin/components/AdminListCard";

type FormState = {
  minMonths: string;
  maxMonths: string;
  translations: Record<string, string>;
};

export default function AdminAgeGroups() {
  const { t, i18n } = useTranslation("common");
  const [ageGroups, setAgeGroups] = useState<AdminAgeGroup[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  const languages = useMemo(() => Object.keys(i18n.options.resources || {}), [i18n.options.resources]);
  const currentLanguage = useMemo(() => (i18n.language || "ar").split("-")[0], [i18n.language]);
  const defaultLanguage = useMemo(() => {
    const fallback = i18n.options.fallbackLng;
    if (typeof fallback === "string") return fallback;
    if (Array.isArray(fallback)) return fallback[0] || "ar";
    if (fallback && typeof fallback === "object") {
      const entry = Object.values(fallback)[0];
      if (Array.isArray(entry)) return entry[0] || "ar";
      if (typeof entry === "string") return entry;
    }
    return "ar";
  }, [i18n.options.fallbackLng]);

  const buildTranslations = (group?: AdminAgeGroup) => {
    return languages.reduce<Record<string, string>>((acc, lng) => {
      const existing = group?.translations.find((item) => item.locale === lng);
      acc[lng] = existing?.title || "";
      return acc;
    }, {});
  };

  const getAgeGroupTitle = (group: AdminAgeGroup) => {
    const direct = group.translations.find((item) => item.locale === currentLanguage)?.title;
    if (direct) return direct;
    const fallback = group.translations.find((item) => item.locale === defaultLanguage)?.title;
    if (fallback) return fallback;
    return group.translations[0]?.title || group.key;
  };

  const [form, setForm] = useState<FormState>({
    minMonths: "",
    maxMonths: "",
    translations: buildTranslations(),
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);

  const loadAgeGroups = () => {
    return fetchAdminAgeGroups()
      .then((data) => setAgeGroups(data))
      .catch(() => setAgeGroups([]));
  };

  useEffect(() => {
    let isMounted = true;
    loadAgeGroups().finally(() => {
      if (!isMounted) return;
    });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      translations: buildTranslations(editingId ? ageGroups.find((g) => g.id === editingId) : undefined),
    }));
  }, [languages, editingId, ageGroups]);

  const resetForm = () => {
    setEditingId(null);
    setForm({ minMonths: "", maxMonths: "", translations: buildTranslations() });
    setError("");
    setSelectedLanguage(defaultLanguage);
  };

  const handleEdit = (group: AdminAgeGroup) => {
    setEditingId(group.id);
    setForm({
      minMonths: String(group.minMonths),
      maxMonths: String(group.maxMonths),
      translations: buildTranslations(group),
    });
    setError("");
    setSelectedLanguage(defaultLanguage);
    setIsModalOpen(true);
  };

  const handleCreate = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const handleLanguageChange = (language: string) => {
    setSelectedLanguage(language);
    setForm((prev) => {
      const current = prev.translations[language];
      if (current && current.trim().length > 0) {
        return prev;
      }
      const fallbackValue = prev.translations[defaultLanguage];
      if (!fallbackValue || fallbackValue.trim().length === 0) {
        return prev;
      }
      return {
        ...prev,
        translations: {
          ...prev.translations,
          [language]: fallbackValue,
        },
      };
    });
  };

  const handleSubmit = async () => {
    setError("");
    const minMonths = Number(form.minMonths);
    const maxMonths = Number(form.maxMonths);

    if (Number.isNaN(minMonths) || Number.isNaN(maxMonths)) {
      setError(t("adminFillRequired"));
      return;
    }

    const translations = languages
      .map((lng) => ({ locale: lng, title: form.translations[lng]?.trim() || "" }))
      .filter((item) => item.title.length > 0);

    if (translations.length === 0) {
      setError(t("adminFillTranslations"));
      return;
    }

    setIsSaving(true);
    try {
      if (editingId) {
        await updateAdminAgeGroup(editingId, {
          minMonths,
          maxMonths,
          translations,
        });
      } else {
        await createAdminAgeGroup({
          minMonths,
          maxMonths,
          translations,
        });
      }
      await loadAgeGroups();
      resetForm();
    } catch {
      setError(t("adminSaveFailed"));
    } finally {
      setIsSaving(false);
    }
  };

  const toggleStatus = async (group: AdminAgeGroup) => {
    await updateAdminAgeGroupStatus(group.id, !group.isActive);
    await loadAgeGroups();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-800">{t("adminAgeGroups")}</h2>
          <p className="text-sm text-gray-500 mt-1">{t("adminAgeGroupManageHint")}</p>
        </div>
        <button
          onClick={handleCreate}
          className="px-5 py-2.5 rounded-xl bg-gradient-to-l from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white text-sm font-semibold shadow-md transition-all flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          {t("adminAddAgeGroup")}
        </button>
      </div>

      {/* Desktop Grid View */}
      <div className="hidden md:block bg-white dark:bg-zinc-900 rounded-2xl border border-gray-100 dark:border-zinc-800/80 overflow-hidden shadow-sm">
        <div className="overflow-x-auto custom-scrollbar">
          <div className="min-w-[650px] divide-y divide-gray-50 dark:divide-zinc-800/50">
            <div className="grid grid-cols-4 gap-4 px-6 py-4 bg-gray-50/50 dark:bg-zinc-800/30 text-xs font-semibold text-gray-500">
              <span>{t("adminAgeGroupName")}</span>
              <span>{t("adminAgeGroupRange")}</span>
              <span>{t("adminStatus")}</span>
              <span className="text-left">{t("adminActions")}</span>
            </div>
            {ageGroups.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-16 h-16 bg-gray-50 dark:bg-zinc-800/60 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-300 dark:text-zinc-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                <p className="text-gray-500 dark:text-zinc-400">{t("adminNoData")}</p>
              </div>
            ) : (
              ageGroups.map((group) => (
                <div key={group.id} className="grid grid-cols-4 gap-4 px-6 py-4 text-sm items-center hover:bg-gray-50/50 dark:hover:bg-zinc-800/30 transition-colors">
                  <span className="text-gray-800 dark:text-zinc-200 font-medium">{getAgeGroupTitle(group)}</span>
                  <span className="text-gray-600 dark:text-zinc-400">{getAgeRangeLabel(group.minMonths, group.maxMonths)}</span>
                  <div>
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
                      group.isActive ? "bg-emerald-100 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-400" : "bg-gray-100 dark:bg-zinc-800 text-gray-600 dark:text-zinc-400"
                    }`}>
                      {group.isActive ? t("adminActive") : t("adminInactive")}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 justify-start" dir="ltr">
                    <button
                      onClick={() => handleEdit(group)}
                      className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-zinc-800 rounded-lg transition-colors"
                      title={t("adminEdit")}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => toggleStatus(group)}
                      className={`p-2 rounded-lg transition-colors ${
                        group.isActive ? "text-amber-500 hover:bg-amber-50 dark:hover:bg-zinc-800" : "text-emerald-500 hover:bg-emerald-50 dark:hover:bg-zinc-800"
                      }`}
                      title={group.isActive ? t("adminDisable") : t("adminEnable")}
                    >
                      {group.isActive ? (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      )}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Mobile Card List View */}
      <div className="block md:hidden space-y-4">
        {ageGroups.length === 0 ? (
          <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-100 dark:border-zinc-800/85 p-12 text-center shadow-sm">
            <div className="w-16 h-16 bg-gray-50 dark:bg-zinc-800/60 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-300 dark:text-zinc-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
              </svg>
            </div>
            <p className="text-gray-500 dark:text-zinc-400">{t("adminNoData")}</p>
          </div>
        ) : (
          ageGroups.map((group) => (
            <AdminListCard
              key={group.id}
              title={getAgeGroupTitle(group)}
              iconFallback="ri-time-line"
              isActive={group.isActive}
              onEdit={() => handleEdit(group)}
              onToggleStatus={() => toggleStatus(group)}
              badges={[
                {
                  label: getAgeRangeLabel(group.minMonths, group.maxMonths),
                  icon: "ri-calendar-todo-line"
                }
              ]}
            />
          ))
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingId ? t("adminEditAgeGroup") : t("adminAddAgeGroup")}
        maxWidth="2xl"
        footer={
          <>
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="px-5 py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium transition-colors"
            >
              {t("adminCancel")}
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSaving}
              className="px-5 py-2.5 rounded-xl bg-teal-500 hover:bg-teal-600 text-white text-sm font-semibold transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {isSaving && (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              )}
              {editingId ? t("adminUpdate") : t("adminCreate")}
            </button>
          </>
        }
      >
        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t("adminMinMonths")}</label>
              <input
                type="number"
                value={form.minMonths}
                onChange={(event) => setForm((prev) => ({ ...prev, minMonths: event.target.value }))}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all"
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t("adminMaxMonths")}</label>
              <input
                type="number"
                value={form.maxMonths}
                onChange={(event) => setForm((prev) => ({ ...prev, maxMonths: event.target.value }))}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all"
                placeholder="12"
              />
            </div>
          </div>

          <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
            <label className="block text-xs font-semibold text-gray-600 mb-3">{t("adminTranslationValue")}</label>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <div className="w-full sm:w-32 flex-shrink-0">
                <LanguageSelect languages={languages} value={selectedLanguage} onChange={handleLanguageChange} />
              </div>
              <input
                value={form.translations[selectedLanguage] || ""}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    translations: { ...prev.translations, [selectedLanguage]: event.target.value },
                  }))
                }
                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all"
                placeholder={t("adminTranslationHint")}
              />
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-600 flex items-center gap-2">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              {error}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
